/**
 * Multi-Party Helper
 * Utilities for multi-party receipt/payment vouchers (CR, BR, CP, BP).
 *
 * Multi-party transactions store partyId: null (like petty) with a partyLines[]
 * array holding per-party data. Reports call expandMultiPartyTransactions()
 * to split them into virtual per-party rows before filtering.
 */

import { buildDeductionLines, buildPaymentDeductionLines, buildAdvanceWhtLine, round2 } from './receiptDeductionsHelper';

/**
 * Expand multi-party transactions into virtual per-party rows for reports.
 * Non-multi-party transactions pass through unchanged.
 *
 * Each partyLine becomes a cloned transaction with:
 *  - partyId, partyName, partyType from the line
 *  - totalAmount = line.amount (or line.netAmount for payments with WHT)
 *  - _isMultiPartyExpanded = true (marker for debugging)
 */
export const expandMultiPartyTransactions = (transactions) => {
  if (!transactions || !Array.isArray(transactions)) return transactions;

  const result = [];
  for (const tran of transactions) {
    if (tran.receiptMode === 'multi-party' && Array.isArray(tran.partyLines) && tran.partyLines.length > 0) {
      // Receipts (CR/BR): totalAmount means CASH received (a line may clear more
      // than that via WHT / bad-debt deductions: gross = cash + WHT + bad debt). Payments
      // keep their existing meaning (totalAmount = gross line amount).
      const isReceipt = tran.tranTypeCode === 'CR' || tran.tranTypeCode === 'BR';
      for (const line of tran.partyLines) {
        const netAmount = line.netAmount != null ? line.netAmount : (line.amount || 0);
        result.push({
          ...tran,
          partyType: line.partyType,
          partyId: line.partyId,
          partyName: line.partyName,
          totalAmount: isReceipt ? netAmount : (line.amount || 0),
          whtAmount: line.whtAmount || 0,
          netAmount,
          // Per-line adjustment (receipt bad debt / payment discount). Receipts:
          // gross = totalAmount + whtAmount + adjustmentAmount; payments: net =
          // totalAmount − whtAmount − adjustmentAmount (`grossAmount` / doc-level
          // `netAmount` are reserved aliases the save code overwrites).
          adjustmentAmount: line.adjustmentAmount || 0,
          _isMultiPartyExpanded: true
        });
      }
    } else {
      result.push(tran);
    }
  }
  return result;
};

/**
 * Build sub-transaction lines for a multi-party RECEIPT (CR/BR).
 * Dr Cash/Bank (cash received), [Dr WHT Receivable / Dr Bad Debts per invoice],
 * Cr each Customer (their gross amount).
 *
 * `deductionAccounts` ({ whtAccount, badDebtAccount }) is passed only when some
 * invoice carries WHT / write-off; without it the output is exactly as before
 * (Dr Cash/Bank total, Cr each customer).
 */
export const buildReceiptSubTransactionLines = (cashOrBankAccount, partyLines, narration, deductionAccounts = null) => {
  const gross = partyLines.reduce((sum, l) => sum + (Number(l.amount) || 0), 0);
  const deducted = deductionAccounts
    ? partyLines.reduce((sum, l) => sum + (Number(l.whtAmount) || 0) + (Number(l.adjustmentAmount) || 0), 0)
    : 0;
  const total = round2(gross - deducted);
  const lines = [];
  if (!deductionAccounts || total > 0) {
    lines.push({
      accountId: cashOrBankAccount.id,
      accountCode: cashOrBankAccount.code || '',
      accountName: cashOrBankAccount.name || '',
      debitAmount: deductionAccounts ? total : gross,
      creditAmount: 0,
      description: `Multi-party receipt - ${narration || ''}`
    });
  }

  for (const pl of partyLines) {
    if (deductionAccounts) {
      lines.push(...buildDeductionLines(pl.invoiceAllocations || [], deductionAccounts, pl.partyName));
    }
    lines.push({
      accountId: pl.partyAccountId,
      accountCode: pl.partyAccountCode || '',
      accountName: `Customer: ${pl.partyName || ''}`,
      debitAmount: 0,
      creditAmount: Number(pl.amount) || 0,
      description: `Receipt from ${pl.partyName || ''} - ${narration || ''}`
    });
  }

  return lines;
};

/**
 * Build sub-transaction lines for a multi-party PAYMENT (CP/BP).
 * Dr each Vendor (gross amount), Cr Cash/Bank (net total), Cr WHT Payable (total WHT if any).
 *
 * `deductionAccounts` ({ whtAccount, adjAccount }) switches to the per-invoice
 * layout: each line's invoice rows post their own WHT / adjustment credit legs,
 * plus one advance-WHT leg for the line's unallocated part (line.advanceWhtAmount),
 * and the bank is credited gross − WHT − adjustment. Without it the output is
 * exactly as before.
 */
export const buildPaymentSubTransactionLines = (cashOrBankAccount, partyLines, whtAccount, narration, deductionAccounts = null) => {
  if (deductionAccounts) {
    const out = [];
    for (const pl of partyLines) {
      out.push({
        accountId: pl.partyAccountId,
        accountCode: pl.partyAccountCode || '',
        accountName: `Vendor: ${pl.partyName || ''}`,
        debitAmount: Number(pl.amount) || 0,
        creditAmount: 0,
        description: `Payment to ${pl.partyName || ''} - ${narration || ''}`
      });
    }
    const net = round2(partyLines.reduce((s, l) =>
      s + (Number(l.amount) || 0) - (Number(l.whtAmount) || 0) - (Number(l.adjustmentAmount) || 0), 0));
    if (net > 0) {
      out.push({
        accountId: cashOrBankAccount.id,
        accountCode: cashOrBankAccount.code || '',
        accountName: cashOrBankAccount.name || '',
        debitAmount: 0,
        creditAmount: net,
        description: `Multi-party payment - ${narration || ''}`
      });
    }
    for (const pl of partyLines) {
      out.push(...buildPaymentDeductionLines(pl.invoiceAllocations || [], deductionAccounts, pl.partyName));
      const adv = buildAdvanceWhtLine(pl.advanceWhtAmount, pl.whtRate, deductionAccounts.whtAccount, pl.partyName);
      if (adv) out.push(adv);
    }
    return out;
  }

  const lines = [];

  // Dr each Vendor (gross amount)
  for (const pl of partyLines) {
    lines.push({
      accountId: pl.partyAccountId,
      accountCode: pl.partyAccountCode || '',
      accountName: `Vendor: ${pl.partyName || ''}`,
      debitAmount: Number(pl.amount) || 0,
      creditAmount: 0,
      description: `Payment to ${pl.partyName || ''} - ${narration || ''}`
    });
  }

  const totalGross = partyLines.reduce((sum, l) => sum + (Number(l.amount) || 0), 0);
  const totalWht = partyLines.reduce((sum, l) => sum + (Number(l.whtAmount) || 0), 0);
  const netTotal = totalGross - totalWht;

  // Cr Cash/Bank (net total)
  lines.push({
    accountId: cashOrBankAccount.id,
    accountCode: cashOrBankAccount.code || '',
    accountName: cashOrBankAccount.name || '',
    debitAmount: 0,
    creditAmount: netTotal,
    description: `Multi-party payment - ${narration || ''}`
  });

  // Cr WHT Payable (total WHT) if any
  if (totalWht > 0 && whtAccount) {
    lines.push({
      accountId: whtAccount.id,
      accountCode: whtAccount.code || '',
      accountName: whtAccount.name || 'WHT Payable',
      debitAmount: 0,
      creditAmount: totalWht,
      description: `WHT on multi-party payment - ${narration || ''}`
    });
  }

  return lines;
};

/**
 * Flatten per-line invoiceAllocations into a single root array with partyId added.
 * This ensures backward-compat with reverseInvoiceAllocations() which reads
 * the root invoiceAllocations[] field.
 */
export const flattenInvoiceAllocations = (partyLines) => {
  const result = [];
  for (const pl of partyLines) {
    if (Array.isArray(pl.invoiceAllocations)) {
      for (const alloc of pl.invoiceAllocations) {
        result.push({
          ...alloc,
          partyId: pl.partyId
        });
      }
    }
  }
  return result;
};
